// Training — visual port of Inku's models + training surface. Stubbed:
// model library cards (LoRAs) + recent training runs.

import {
  Activity,
  CheckCircle2,
  Clock,
  Loader2,
  Plus,
  Sparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

interface ModelSeed {
  id: string;
  name: string;
  type: "PRODUCT" | "STYLE";
  brand: string;
  thumb: string;
  status: "ready" | "training" | "queued";
  steps: number;
  createdAt: string;
}

interface TrainingRun {
  id: string;
  modelName: string;
  status: "running" | "succeeded" | "failed";
  step: number;
  total: number;
  startedAt: string;
}

const MODELS: ModelSeed[] = [
  {
    id: "m1",
    name: "BRAME bottle · v3",
    type: "PRODUCT",
    brand: "BRAME",
    thumb: "linear-gradient(160deg, #6E2A14 0%, #C7935E 70%, #F3EADB 100%)",
    status: "ready",
    steps: 1500,
    createdAt: "2d ago",
  },
  {
    id: "m2",
    name: "Loop bowl · v2",
    type: "PRODUCT",
    brand: "Loop Foods",
    thumb: "linear-gradient(160deg, #2E7D32 0%, #76C893 70%, #F2EBC1 100%)",
    status: "ready",
    steps: 1200,
    createdAt: "1w ago",
  },
  {
    id: "m3",
    name: "Slow morning · lifestyle",
    type: "STYLE",
    brand: "BRAME",
    thumb: "linear-gradient(135deg, #FFB17A 0%, #FFD08A 80%, #F3EADB 100%)",
    status: "ready",
    steps: 800,
    createdAt: "2w ago",
  },
  {
    id: "m4",
    name: "Veldt evening · style",
    type: "STYLE",
    brand: "Veldt",
    thumb: "linear-gradient(135deg, #1A007F 0%, #6B4FFF 60%, #B5A4FF 100%)",
    status: "training",
    steps: 600,
    createdAt: "in progress",
  },
  {
    id: "m5",
    name: "BRAME limited · v1",
    type: "PRODUCT",
    brand: "BRAME",
    thumb: "linear-gradient(135deg, #B5A4FF 0%, #5A2BFF 70%, #1A007F 100%)",
    status: "queued",
    steps: 0,
    createdAt: "queued",
  },
];

const RUNS: TrainingRun[] = [
  {
    id: "r1",
    modelName: "Veldt evening · style",
    status: "running",
    step: 600,
    total: 1000,
    startedAt: "20m ago",
  },
  {
    id: "r2",
    modelName: "BRAME limited · v1",
    status: "running",
    step: 0,
    total: 1500,
    startedAt: "5m ago",
  },
  {
    id: "r3",
    modelName: "BRAME bottle · v3",
    status: "succeeded",
    step: 1500,
    total: 1500,
    startedAt: "2d ago",
  },
  {
    id: "r4",
    modelName: "Slow morning · lifestyle",
    status: "succeeded",
    step: 800,
    total: 800,
    startedAt: "2w ago",
  },
];

export function TrainingPage() {
  const { setToast } = useStore();
  const tellDemoOnly = (text: string) => () => setToast({ kind: "ok", text });

  return (
    <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10 py-12 space-y-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Workspace
          </p>
          <h1 className="font-freight text-5xl font-light tracking-tight leading-none">
            Training
          </h1>
          <p className="text-muted-foreground max-w-xl">
            Custom models and lifestyle styles, trained on your brand
            assets. Each one becomes a tool the Creative Director can call.
          </p>
        </div>
        <Button
          onClick={tellDemoOnly("Training is not wired up in this demo.")}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Train new model
        </Button>
      </div>

      {/* Active runs strip */}
      <section className="space-y-3">
        <div className="flex items-center gap-3">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            Recent runs
          </h2>
          <span className="font-mono text-xs text-muted-foreground">
            {String(RUNS.length).padStart(2, "0")}
          </span>
        </div>
        <div className="rounded-lg border border-border bg-card overflow-hidden shadow-card">
          {RUNS.map((r, i) => (
            <div
              key={r.id}
              className={cn(
                "px-5 py-3.5 flex items-center gap-4",
                i < RUNS.length - 1 && "border-b border-border"
              )}
            >
              <span className="font-mono text-xs text-muted-foreground w-8">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{r.modelName}</p>
                <p className="text-xs text-muted-foreground">
                  Started {r.startedAt}
                </p>
              </div>
              <div className="w-48">
                <RunProgress run={r} />
              </div>
              <RunStatusBadge status={r.status} />
            </div>
          ))}
        </div>
      </section>

      {/* Models grid */}
      <section className="space-y-3">
        <div className="flex items-center gap-3">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            Models
          </h2>
          <span className="font-mono text-xs text-muted-foreground">
            {String(MODELS.length).padStart(2, "0")}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {MODELS.map((m) => (
            <div
              key={m.id}
              className="rounded-xl border border-border bg-card overflow-hidden shadow-card hover:shadow-card-hover transition-shadow"
            >
              <div
                className="aspect-[16/10] w-full"
                style={{ background: m.thumb }}
              />
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
                    {m.brand} · {m.type === "PRODUCT" ? "Product" : "Lifestyle"}
                  </p>
                  <ModelStatusBadge status={m.status} />
                </div>
                <h3 className="text-sm font-medium leading-snug">{m.name}</h3>
                <div className="flex items-center justify-between text-xs text-muted-foreground/80">
                  <span>{m.steps.toLocaleString()} steps</span>
                  <span>{m.createdAt}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function RunProgress({ run }: { run: TrainingRun }) {
  const pct = Math.min(100, Math.round((run.step / run.total) * 100));
  if (run.status !== "running") {
    return (
      <p className="text-xs text-muted-foreground/70 text-right">
        {run.total.toLocaleString()} / {run.total.toLocaleString()} steps
      </p>
    );
  }
  return (
    <div className="space-y-1">
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-[hsl(var(--inku-blue))] rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground/70 text-right">
        {run.step.toLocaleString()} / {run.total.toLocaleString()}
      </p>
    </div>
  );
}

function RunStatusBadge({ status }: { status: TrainingRun["status"] }) {
  if (status === "running") {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--inku-blue))]">
        <Loader2 className="h-3 w-3 animate-spin" />
        Running
      </span>
    );
  }
  if (status === "succeeded") {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-green-700 dark:text-green-400">
        <CheckCircle2 className="h-3 w-3" />
        Succeeded
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-destructive">
      Failed
    </span>
  );
}

function ModelStatusBadge({ status }: { status: ModelSeed["status"] }) {
  if (status === "ready") {
    return (
      <span className="inline-flex items-center gap-1 rounded font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-green-100 text-green-900 dark:bg-green-950/40 dark:text-green-200">
        Ready
      </span>
    );
  }
  if (status === "training") {
    return (
      <span className="inline-flex items-center gap-1 rounded font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[hsl(var(--inku-blue))/.10] text-[hsl(var(--inku-blue))]">
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
        Training
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-muted text-muted-foreground">
      <Clock className="h-2.5 w-2.5" />
      Queued
    </span>
  );
}
